import { type MouseEvent } from 'react';

import type { Overlay } from '../../core/components/overlay-tooltip/types.js';
import { useOverlayEventHandlers } from '../../core/hooks/useOverlayEventHandlers.js';
import { LABELS_DISPLAY } from '../constants.js';
import { useSetOverlayState } from './useSetOverlayState.js';
import { useFormatter } from '../context/FormatterContext.js';
import { useLabelsDisplay } from '../context/LabelsDisplayContext.js';
import { useTreeMapInternalData } from '../context/TreeMapInternalDataContext.js';
import { useClusterNodesState, useLeafNodesState } from '../store/selectors.js';
import { buildOverlayTooltipData } from '../utils/build-overlay-tooltip-data.js';
import { getTooltipData } from '../utils/get-tooltip-data.js';

export const useOverlayHandlers = (overlay: Overlay) => {
  const hierarchyLeafNodes = useLeafNodesState();
  const hierarchyClusterNodes = useClusterNodesState();
  const labelsDisplay = useLabelsDisplay();
  const { setToolbarVisible } = useSetOverlayState();
  const formatter = useFormatter();
  const { unit } = useTreeMapInternalData();

  const parentLabels: string[] = [LABELS_DISPLAY.All, LABELS_DISPLAY.Clusters];
  const hasParentLabels = parentLabels.includes(labelsDisplay);

  const getTooltipDataFromEvent = (event: MouseEvent<HTMLDivElement>) => {
    const position = overlay.getAdjustedPositionsFromAbsolute(
      event.clientX,
      event.clientY,
    );
    const relativePosition = {
      x: position.relativeX ?? 0,
      y: position.relativeY ?? 0,
    };
    const tooltipData = getTooltipData(
      hierarchyLeafNodes,
      hierarchyClusterNodes,
      relativePosition,
      hasParentLabels,
    );
    return tooltipData
      ? buildOverlayTooltipData(tooltipData, formatter, unit)
      : undefined;
  };

  return useOverlayEventHandlers(overlay, {
    onToolbarVisibilityChange: setToolbarVisible,
    getTooltipData: getTooltipDataFromEvent,
  });
};
